import React, { useEffect, useRef } from "react";
import {
  useApplyVideoObjectFit,
  useAudioVideo,
  useLocalVideo,
  useMeetingManager,
} from "amazon-chime-sdk-component-library-react";
import { VideoUITile } from "./VideoUITile";
import { extractRoomMemberData } from "@platform/features/video/utils/roomMemberDataUtils";
import { useVideo } from "@platform/features/video/providers/VideoContext";

interface Props {
  id?: string;
  isWithContentShare?: boolean;
  tileIndex?: number;
  totalTiles?: number;
  nameplate?: string;
  className?: string;
}

export const VideoUITileLocalVideoContainer: React.FC<
  React.PropsWithChildren<Props>
> = ({
  tileIndex,
  totalTiles,
  nameplate,
  className,
  isWithContentShare,
  ...rest
}) => {
  const { tileId, isVideoEnabled } = useLocalVideo();

  const audioVideo = useAudioVideo();

  const meetingManager = useMeetingManager();

  const videoEl = useRef<HTMLVideoElement>(null);

  useApplyVideoObjectFit(videoEl);

  const { userName, roomMembersData } = useVideo();

  const { roomMemberData } = userName
    ? extractRoomMemberData({ externalUserId: userName }, roomMembersData)
    : { roomMemberData: null };

  // Get local attendee ID for active speaker detection
  const localAttendeeId =
    meetingManager.meetingSession?.configuration.credentials?.attendeeId ||
    null;

  useEffect(() => {
    if (!audioVideo || !tileId || !videoEl.current || !isVideoEnabled) {
      return;
    }

    audioVideo.bindVideoElement(tileId, videoEl.current);

    return () => {
      const tile = audioVideo.getVideoTile(tileId);
      if (tile) {
        audioVideo.unbindVideoElement(tileId);
      }
    };
  }, [audioVideo, tileId, isVideoEnabled]);

  return (
    <VideoUITile
      className={className}
      nameplate={nameplate}
      isWithContentShare={isWithContentShare}
      ref={videoEl}
      isEnabled={isVideoEnabled}
      tileIndex={tileIndex}
      totalTiles={totalTiles}
      attendeeId={localAttendeeId || undefined}
      roomMemberData={roomMemberData}
      {...rest}
    />
  );
};

export default VideoUITileLocalVideoContainer;
